/**
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/dashboard/stat-cards.tsx — its "New
 * Orders" and "Sales" cards, fed by `GET /api/orders/reports/order-trend` (ordering-admin.md §12)
 * instead of the template's constants. One promise, two cards: the count and the value are the
 * API's own `totals`, the bars its own zero-filled daily buckets. Nothing is summed here.
 */
import { PiClipboardTextDuotone as Clipboard, PiCurrencyInrDuotone as Rupee } from "react-icons/pi";
import { TileBoundary } from "./TileBoundary";
import { TrendStatCard, UnreadableStatCard, countOf, formatCount } from "./TrendStatCard";
import { money, type OrderTrendReport } from "../../lib/services/ordering";
import { formatMoney } from "../../lib/format";

function unreadable(error: unknown) {
  const fallback = "Could not be read — this is not the same as no orders.";
  return (
    <>
      <UnreadableStatCard title="Orders placed" icon={<Clipboard />} error={error} fallback={fallback} />
      <UnreadableStatCard title="Order value" icon={<Rupee />} error={error} fallback={fallback} />
    </>
  );
}

export function OrderTrendCards({ resolve, period }: { resolve: Promise<OrderTrendReport>; period: string }) {
  return (
    <TileBoundary resolve={resolve} errorFallback={unreadable} fallbackClassName="h-[196px]">
      {(report) => (
        <>
          <TrendStatCard
            title="Orders placed"
            metric={formatCount(report.totals.orderCount)}
            icon={<Clipboard />}
            points={report.buckets.map((b) => ({ periodStart: b.periodStart, value: money(b.orderCount) }))}
            format={(value) => countOf(value, "order")}
            period={period}
            footer="Every order placed, cancellations excluded"
          />
          <TrendStatCard
            title="Order value"
            metric={formatMoney(money(report.totals.orderedValue))}
            icon={<Rupee />}
            points={report.buckets.map((b) => ({ periodStart: b.periodStart, value: money(b.orderedValue) }))}
            format={formatMoney}
            period={period}
            footer="Order totals, GST and freight included"
          />
        </>
      )}
    </TileBoundary>
  );
}
